import React from "react";
import { Reveal } from "../motion/Reveal";
import { Award, ShieldCheck, IndianRupee, Truck, FileCheck2, Headset } from "lucide-react";

const PILLARS = [
  {
    Icon: Award,
    title: "25+ Years Experience",
    body: "Two-and-a-half decades of enterprise IT deployments across India — before it was fashionable to say ‘digital transformation’.",
    image: "https://images.unsplash.com/photo-1509048191080-d2984bad6ae5?w=1200&q=80",
    position: "center",
  },
  {
    Icon: ShieldCheck,
    title: "Genuine Products",
    body: "Every SKU sourced through authorised principal channels. Serial-traceable warranties, always.",
    image: "https://images.pexels.com/photos/4483610/pexels-photo-4483610.jpeg?w=1200",
    position: "center",
  },
  {
    Icon: IndianRupee,
    title: "Corporate Pricing",
    body: "Volume-negotiated pricing that scales with your rollout — from a 12-seat office to a 12-city network.",
    image: "https://images.pexels.com/photos/3183197/pexels-photo-3183197.jpeg?w=1200",
    position: "center",
  },
  {
    Icon: Truck,
    title: "PAN India Delivery",
    body: "Logistics tuned for tier-1 metros and tier-3 field sites alike. Delivered, installed, signed-off.",
    image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1200&q=80",
    position: "center",
  },
  {
    Icon: FileCheck2,
    title: "Government Tender Expertise",
    body: "GeM listings, EMD, BOQ and technical compliance — handled end-to-end with a straight face.",
    image: "https://images.unsplash.com/photo-1568992687947-868a62a9f521?w=1200&q=80",
    position: "center",
  },
  {
    Icon: Headset,
    title: "Technical Support",
    body: "Same-day incident response with escalation trees. Engineers who show up, not scripts that don’t.",
    image: "https://images.pexels.com/photos/8867482/pexels-photo-8867482.jpeg?w=1200",
    position: "center",
  },
];

export default function WhyChooseUs() {
  return (
    <section id="why" className="bg-[#FAFAFA] py-24 lg:py-32" data-testid="why-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14 lg:mb-20">
          <Reveal className="lg:col-span-6">
            <div className="section-label mb-6 !text-[#9A9A9A]">Why Choose DB Computronics</div>
            <h2 className="font-display text-[#222222] text-[40px] sm:text-[56px] lg:text-[76px] leading-[0.95] tracking-[-0.03em]">
              Six reasons<br />
              you'll <span className="text-[#A4161A]">stay</span>.
            </h2>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-5 lg:col-start-8 flex items-end">
            <p className="text-[#444444] max-w-[48ch] leading-relaxed font-body text-[15px] lg:text-[16px]">
              Vendors sell you products. Integrators sell you projects. We sell you a decade of
              uptime — measured, monitored, and made to matter.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {PILLARS.map(({ Icon, title, body, image }, i) => (
            <Reveal key={title} delay={i * 0.06}>
              <article
                className="group relative rounded-xl overflow-hidden h-full min-h-[380px] lg:min-h-[420px] border border-[#222222]/10 transition-all duration-500 hover:shadow-[0_28px_80px_-24px_rgba(0,0,0,0.55)]"
                data-testid={`pillar-${i}`}
              >
                {/* Background image */}
                <img
                  src={image}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 w-full h-full object-cover scale-[1.02] group-hover:scale-[1.10] transition-transform duration-[1400ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
                  loading="lazy"
                />
                {/* Dark gradient overlay for text contrast */}
                <div
                  className="absolute inset-0 bg-gradient-to-t from-[#111]/95 via-[#111]/60 to-[#111]/25 group-hover:from-[#111]/98 group-hover:via-[#111]/70 transition-all duration-700"
                  aria-hidden="true"
                />
                {/* Red accent glow on hover (top-right) */}
                <div
                  className="absolute -top-24 -right-24 w-56 h-56 rounded-full bg-[#A4161A]/0 group-hover:bg-[#A4161A]/25 blur-3xl transition-all duration-700"
                  aria-hidden="true"
                />

                {/* Content */}
                <div className="relative h-full flex flex-col justify-between p-7 lg:p-9">
                  <div className="flex items-center justify-between">
                    <div className="chapter-num !text-[80px] lg:!text-[104px] !leading-none !text-white/20">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="w-12 h-12 rounded-lg bg-[#FAFAFA] text-[#A4161A] flex items-center justify-center transition-all duration-500 group-hover:bg-[#A4161A] group-hover:text-[#FAFAFA] shadow-[0_10px_30px_-10px_rgba(0,0,0,0.5)]">
                      <Icon size={20} strokeWidth={1.8} />
                    </div>
                  </div>

                  <div>
                    <div className="section-label mb-4 !text-[#FAFAFA]/70">
                      Reason · {String(i + 1).padStart(2, "0")}
                    </div>
                    <h3 className="font-display text-[#FAFAFA] text-[22px] lg:text-[28px] leading-tight tracking-tight mb-3">
                      {title}
                    </h3>
                    <p className="font-body text-[#FAFAFA]/85 text-[13px] lg:text-[14px] leading-relaxed max-w-[42ch]">
                      {body}
                    </p>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
